import { ReactNode } from 'react'

import { Anchor, Card, CardProps, Divider, Stack, Text } from '@mantine/core'
import { observer } from 'mobx-react-lite'

import { FaIcon } from '#components/FaIcon'
import { useNavigation } from '#hooks'
import { useMst } from '#state'
import { IListing } from '#state/types'

import { DocumentRow } from './DocumentRow'

import styles from './DocumentCard.module.css'

const MAX_ITEMS = 4

type CardShellProps = {
  title: string
  cardProps?: Partial<CardProps>
  children: ReactNode
}

function CardShell({ title, cardProps, children }: CardShellProps) {
  return (
    <Stack miw={200} maw={400} flex="1" w="100%">
      <Text fw="bold">{title}</Text>
      <Card className={styles.card} h={285} pt={0} pb={0} px={0} {...cardProps}>
        {children}
      </Card>
    </Stack>
  )
}

type DocumentCardProps = {
  title: string
  folderId: string
  items: IListing[]
  isLoading: boolean
  emptyState: {
    icon: `fa-${string}`
    title: string
    description: string
  }
}

export const DocumentCard = observer(function DocumentCard({
  title,
  folderId,
  items,
  isLoading,
  emptyState,
}: DocumentCardProps) {
  const { navigateToFolder } = useNavigation()
  const { trackLinkClicked } = useMst()
  const displayItems = items.slice(0, MAX_ITEMS)

  if (isLoading) {
    return (
      <CardShell title={title}>
        <Stack justify="center" align="center" h="100%" py={30}>
          <Text c="dimmed">Loading...</Text>
        </Stack>
      </CardShell>
    )
  }

  if (items.length === 0) {
    return (
      <CardShell
        title={title}
        cardProps={{
          py: 30,
          px: 20,
        }}
      >
        <Stack
          className={styles.stack}
          justify="center"
          align="center"
          h="100%"
        >
          <FaIcon icon={emptyState.icon} faIconSet="fa-regular" size="lg" />
          <Text fw="bold" ta="center">
            {emptyState.title}
          </Text>
          <Text ta="center">{emptyState.description}</Text>
        </Stack>
      </CardShell>
    )
  }

  return (
    <CardShell title={title}>
      <Stack justify="space-between" mih="100%" gap={0}>
        <Stack className={styles.listing} gap={0}>
          {displayItems.map((listing) => (
            <DocumentRow key={listing.id} listing={listing} />
          ))}
          <Divider className={styles.divider} />
        </Stack>
        <Stack className={styles.footer} gap={0}>
          {displayItems.length !== MAX_ITEMS && (
            <Divider className={styles.divider} />
          )}
          <Anchor
            className={styles.anchor}
            mih={37}
            pb={10}
            pt={10}
            ta="center"
            size="sm"
            onClick={() => {
              trackLinkClicked({
                source: 'dashboard',
                linkType: 'internal',
                to: 'collection',
                id: folderId,
              })
              navigateToFolder(folderId)
            }}
          >
            View All
          </Anchor>
        </Stack>
      </Stack>
    </CardShell>
  )
})
